import Loading from "@/pages/loading";
import { APP_ROUTES, useAppDispatch, useAppSelector } from "@/utils";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useMessagedAsyncFn } from "@payping/web-components";
import { bnplConsumerSetPlan } from "@payping/redux/actions";
import { dashboardPersistReduxActions } from "@/redux/actions/dashboardPersist";
import PayUnAvailable from "../Unavailable";
import PaySetPlanUnAvailable from "./UnAvailable";
import PaySetPlanNoCreditAvailable from "./NoCreditAvailable";
import PaySetPlanSalrayPhoneNotFound from "./SalaryPhoneNotFound";
import ActiveInstallmentLimit from "./ActiveInstallmentLimit";
import InstallmentLimitReached from "./InstallmentLimitReached";
import DelayedInstallmentLimit from "./DelayedInstallmentLimit";
import InprogressLimit from "./InprogressLimit";

const PaySetPlanPage = () => {
  const [searchParams] = useSearchParams();
  const trackingCode = searchParams.get("trackingCode") || "";
  const planId = Number.isNaN(parseInt(searchParams.get("planId") ?? "", 10))
    ? 0
    : parseInt(searchParams.get("planId") ?? "", 10);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const trackingCodesSetPlanedOrders = useAppSelector((state) => state.dashboardPersist.setPlanedTrackingCodes);
  const trackingCodeIncluded = trackingCodesSetPlanedOrders.includes(trackingCode);

  const [setPlanState, setPlan] = useMessagedAsyncFn(async () => {
    try {
      if (trackingCodeIncluded) {
        navigate(`${APP_ROUTES.pay.getOrder.root.url}?trackingCode=${trackingCode}`);
        return null;
      }
      const res = await dispatch(bnplConsumerSetPlan({ planId, trackingCode }, { isRaw: true }));
      dispatch(dashboardPersistReduxActions.addTrackingCodeToSetPlanedOrders(trackingCode));
      navigate(`${APP_ROUTES.pay.getOrder.root.url}?trackingCode=${trackingCode}`);
      return res;
    } catch (e: any) {
      // Check if e is an instance of Response and if the status is 400
      if (e.status === 400) {
        try {
          const parsedError = await e.json();
          throw parsedError;
        } catch (jsonError: any) {
          if (jsonError.status === 400) {
            throw jsonError;
          }
          throw e; // Fallback if parsing JSON fails
        }
      }

      throw e;
    }
  }, [trackingCodeIncluded, planId, trackingCode, dispatch, navigate]);

  useEffect(() => {
    if (!planId && trackingCode) {
      navigate(`${APP_ROUTES.pay.choosePlan.url}?trackingCode=${trackingCode}`);
      return;
    }
    if (!planId && !trackingCode) {
      navigate(APP_ROUTES.home.overview.url);
      return;
    }
    if (trackingCodesSetPlanedOrders.includes(trackingCode)) {
      navigate(`${APP_ROUTES.pay.getOrder.root.url}?trackingCode=${trackingCode}`);
      return;
    }
    setPlan();
  }, [navigate, planId, setPlan, trackingCode, trackingCodesSetPlanedOrders]);

  // no credit available
  if ((setPlanState.error as any)?.key === 102) {
    return <PaySetPlanNoCreditAvailable />;
  }

  // salary phone number not found
  if ((setPlanState.error as any)?.key === 117) {
    return <PaySetPlanSalrayPhoneNotFound />;
  }

  if (setPlanState.error?.name === "ServerError") {
    return (
      <PayUnAvailable
        onRetry={() => {
          setPlan();
        }}
        onRetryLoading={setPlanState.loading}
      />
    );
  }

  const refreshPage = () => {
    window.location.reload();
  };

  if ((setPlanState.error as any)?.key === 113) {
    return <ActiveInstallmentLimit errorMessages={(setPlanState.error as any)?.errors ?? []} />;
  }

  if ((setPlanState.error as any)?.key === 114) {
    return <InstallmentLimitReached />;
  }

  if ((setPlanState.error as any)?.key === 115) {
    return <DelayedInstallmentLimit />;
  }

  if ((setPlanState.error as any)?.key === 116) {
    return <InprogressLimit planId={planId} trackingCode={trackingCode} />;
  }

  if ((setPlanState.error as any)?.status === 400) {
    return (
      <PaySetPlanUnAvailable
        errorMessages={(setPlanState.error as any)?.errors ?? []}
        onRetry={() => {
          refreshPage();
        }}
      />
    );
  }

  return <Loading />;
};

export default PaySetPlanPage;
